import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import bgDesktop from "../images/page-1-2.jpg";
import bgMobile from "../images/page-1-mobile.jpg";

/* ============================================
   Originalbildgröße Desktop
============================================ */
const ORIGINAL_W = 2048;
const ORIGINAL_H = 1151;

/* ============================================
   Desktop Hotspots (px)
============================================ */
const emailRectPx = [
    (0.642422 - 0.04) * ORIGINAL_W,
    0.781434 * ORIGINAL_H,
    (0.642422 - 0.04 + 0.106016) * ORIGINAL_W,
    (0.781434 + 0.03) * ORIGINAL_H,
];
const wwwRectPx = [
    (0.758203 - 0.05) * ORIGINAL_W,
    0.781434 * ORIGINAL_H,
    (0.758203 - 0.05 + 0.1178906) * ORIGINAL_W,
    (0.781434 + 0.03) * ORIGINAL_H,
];

/* Desktop: Objekt-fit-cover → Prozentwerte */
function rectForCoverToPercent([x1, y1, x2, y2], containerW, containerH) {
    const scale = Math.max(containerW / ORIGINAL_W, containerH / ORIGINAL_H);
    const renderedW = ORIGINAL_W * scale;
    const renderedH = ORIGINAL_H * scale;
    const offsetX = (containerW - renderedW) / 2;
    const offsetY = (containerH - renderedH) / 2;

    const rx1 = x1 * scale + offsetX;
    const ry1 = y1 * scale + offsetY;
    const rw = (x2 - x1) * scale;
    const rh = (y2 - y1) * scale;

    return {
        left: `${(rx1 / containerW) * 100}%`,
        top: `${(ry1 / containerH) * 100}%`,
        width: `${(rw / containerW) * 100}%`,
        height: `${(rh / containerH) * 100}%`,
    };
}

/* ============================================
   MOBILE Hotspot (px, aus Originalbild)
============================================ */
const MOB_H = 864;
const emailRectMobPx = [900, 690, 1170, 740];

function rectPxToRenderedPx([x1, y1, x2, y2], scale) {
    return {
        left: x1 * scale,
        top: y1 * scale,
        width: (x2 - x1) * scale,
        height: (y2 - y1) * scale,
    };
}

/* ---------------------------------------------------
   DESKTOP VIEW (unverändert außer Ordnung)
--------------------------------------------------- */
function DesktopView() {
    const wrapperRef = useRef(null);
    const [emailBox, setEmailBox] = useState({});
    const [wwwBox, setWwwBox] = useState({});

    useLayoutEffect(() => {
        const recompute = () => {
            const el = wrapperRef.current;
            if (!el) return;

            const w = el.clientWidth;
            const h = el.clientHeight;
            setEmailBox(rectForCoverToPercent(emailRectPx, w, h));
            setWwwBox(rectForCoverToPercent(wwwRectPx, w, h));
        };

        recompute();

        let ro = new ResizeObserver(recompute);
        ro.observe(wrapperRef.current);
        return () => ro.disconnect();
    }, []);

    return (
        <div
            ref={wrapperRef}
            style={{
                position: "relative",
                width: "100vw",
                height: "100vh",
                overflow: "hidden",
            }}
        >
            <img
                src={bgDesktop}
                alt=""
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    pointerEvents: "none",
                }}
            />

            {/* Hotspot: Mail */}
            <a
                href="mailto:mail@tw-a.net"
                style={{
                    position: "absolute",
                    ...emailBox,
                    display: "block",
                }}
                title="E-Mail"
            />

            {/* Hotspot: Website */}
            <a
                href="https://tw-a.net"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    position: "absolute",
                    ...wwwBox,
                    display: "block",
                }}
                title="Website"
            />
        </div>
    );
}

/* ---------------------------------------------------
   NEW → TABLET VIEW (wie Page2-Tablet)
--------------------------------------------------- */
function TabletView() {
    const containerRef = useRef(null);
    const [state, setState] = useState({ w: 0, h: 0, scale: 1 });
    const [emailBox, setEmailBox] = useState({});
    const [wwwBox, setWwwBox] = useState({});

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const recompute = () => {
            const h = el.clientHeight;
            const scale = h / ORIGINAL_H;
            const w = Math.round(ORIGINAL_W * scale);

            setState({ w, h, scale });

            // Rechts starten (wie Page2)
            el.scrollLeft = Math.max(0, w - el.clientWidth);

            const conv = ([x1, y1, x2, y2]) => {
                const left = x1 * scale;
                const top = y1 * scale;
                const width = (x2 - x1) * scale;
                const height = (y2 - y1) * scale;
                return { left, top, width, height };
            };

            setEmailBox(conv(emailRectPx));
            setWwwBox(conv(wwwRectPx));
        };

        recompute();
        const ro = new ResizeObserver(recompute);
        ro.observe(el);

        return () => ro.disconnect();
    }, []);

    return (
        <div
            ref={containerRef}
            style={{
                width: "100vw",
                height: "100vh",
                overflowX: "auto",
                overflowY: "hidden",
                WebkitOverflowScrolling: "touch",
                background: "#000",
                position: "relative",
            }}
        >
            <div
                style={{
                    position: "relative",
                    width: `${state.w}px`,
                    height: "100%",
                }}
            >
                <img
                    src={bgDesktop}
                    alt=""
                    style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        height: "100%",
                        width: "auto",
                        pointerEvents: "none",
                    }}
                    draggable={false}
                />

                {/* Mail Hotspot */}
                <a
                    href="mailto:mail@tw-a.net"
                    style={{
                        position: "absolute",
                        left: `${emailBox.left}px`,
                        top: `${emailBox.top}px`,
                        width: `${emailBox.width}px`,
                        height: `${emailBox.height}px`,
                        display: "block",
                    }}
                >
                    <span className="sr-only">E-Mail an mail@tw-a.net</span>
                </a>

                {/* Web Hotspot */}
                <a
                    href="https://tw-a.net"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        position: "absolute",
                        left: `${wwwBox.left}px`,
                        top: `${wwwBox.top}px`,
                        width: `${wwwBox.width}px`,
                        height: `${wwwBox.height}px`,
                        display: "block",
                    }}
                >
                    <span className="sr-only">Website tw-a.net öffnen</span>
                </a>

            </div>
        </div>
    );
}

/* ---------------------------------------------------
   MOBILE VIEW (unverändert)
--------------------------------------------------- */
function MobileView() {
    const scrollRef = useRef(null);
    const imgRef = useRef(null);
    const [emailBoxMob, setEmailBoxMob] = useState(null);

    useEffect(() => {
        const sc = scrollRef.current;
        const img = imgRef.current;
        if (!sc || !img) return;

        const compute = () => {
            const imgRect = img.getBoundingClientRect();
            const scale = imgRect.height / MOB_H;

            const b = rectPxToRenderedPx(emailRectMobPx, scale);

            setEmailBoxMob({
                position: "absolute",
                left: b.left - 100 + "px",
                top: b.top - 10 + "px",
                width: b.width - 40 + "px",
                height: b.height - 20 + "px",
            });

            sc.scrollLeft = Math.max(0, imgRect.width - sc.clientWidth);
        };

        img.complete ? compute() : img.addEventListener("load", compute, { once: true });

        const ro = new ResizeObserver(compute);
        ro.observe(sc);

        return () => ro.disconnect();
    }, []);

    return (
        <div
            ref={scrollRef}
            style={{
                width: "100vw",
                height: "100vh",
                overflowX: "auto",
                overflowY: "hidden",
                WebkitOverflowScrolling: "touch",
                position: "relative",
            }}
        >
            <img
                ref={imgRef}
                src={bgMobile}
                alt=""
                style={{ height: "100%", width: "auto", display: "block" }}
            />

            {emailBoxMob && (
                <a
                    href="mailto:mail@tw-a.net"
                    style={emailBoxMob}
                    title="E-Mail"
                />
            )}
        </div>
    );
}

/* ---------------------------------------------------
   MAIN COMPONENT – VIEW SWITCH
--------------------------------------------------- */
export default function Page1() {
    const [vw, setVw] = useState(
        () => (typeof window !== "undefined" ? window.innerWidth : 1920)
    );

    useEffect(() => {
        const onResize = () => setVw(window.innerWidth);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    if (vw < 768) return <MobileView />;
    if (vw <= 1024) return <TabletView />;
    return <DesktopView />;
}
